import { triggerEvent } from './util/event-handler';

// ============================================================================
// Antumo StatusOverlay
// ============================================================================
//
// Blocking, centred status card for states the user has to wait out or decide
// on: reconnecting, a long operation with progress, a countdown to the next
// retry, success before a redirect, an error with a decision.
//
//     const s = StatusOverlay.show({ title: 'Łączę ponownie', text: 'Zwykle kilka sekund.' });
//     s.update({ tone: 'warning', title: 'Serwer nie odpowiada', text: 'Ponawiam za {s} s.', countdown: 5 });
//     s.update({ tone: 'success', title: 'Nowa wersja', text: 'Wczytuję stronę…' });
//     s.hide();
//
// Pure DOM, no framework: it must work while the page framework cannot render
// (Blazor Server with a dropped circuit drives it from the reconnect modal).
// `attach(el)` adopts an element that already exists in the page (for Blazor:
// the server-rendered <dialog id="components-reconnect-modal">).
//
// CSS contract: components/app/_status-overlay.scss (.antumo-status*).
//
// Events (bubble from the backdrop element):
//   antumo:status:shown   after insertion
//   antumo:status:hidden  after removal / hiding
// ============================================================================

export type StatusTone = 'neutral' | 'warning' | 'success' | 'error';
export type StatusTheme = 'light' | 'dark';
export type StatusLayout = 'stacked' | 'inline';
export type StatusIndicator = 'ring' | 'pulse' | 'dots';
export type StatusIconName = 'check' | 'x' | 'alert' | 'lock' | 'wifi-off' | 'refresh' | 'cloud-off';

export interface StatusAction {
  label: string;
  /** DS button variant. Default 'secondary'. */
  variant?: 'primary' | 'secondary';
  onClick?: (overlay: StatusOverlay) => void;
}

export interface StatusOverlayOptions {
  /** Heading. Required. */
  title: string;
  /** Body copy. `{s}` is replaced by the live countdown seconds. */
  text?: string;
  tone?: StatusTone;
  theme?: StatusTheme;
  layout?: StatusLayout;
  /** Default 'ring'. Inline layout always uses the ring. */
  indicator?: StatusIndicator;
  /** 0..1 fills the ring (and the edge bar). null/undefined = indeterminate. */
  progress?: number | null;
  /** Seconds to count down (ring empties, `{s}` ticks). Restarted by every update that sets it. */
  countdown?: number | null;
  /** Icon in place of the brand mark. success → 'check', error → 'alert' by default. */
  icon?: StatusIconName | null;
  /** Brand mark in the indicator while nothing else is shown. Default true. */
  showMark?: boolean;
  /** Thin progress bar on the bottom edge of the card. Default false. */
  edge?: boolean;
  actions?: StatusAction[];
  /** Render inside this element instead of the viewport (position: absolute). */
  container?: HTMLElement | null;
  /** Called when the countdown reaches zero. */
  onCountdownElapsed?: (overlay: StatusOverlay) => void;
}

const EVENT_SHOWN = 'antumo:status:shown';
const EVENT_HIDDEN = 'antumo:status:hidden';
const RING_C = 150.8;

const SVG = (body: string, cls: string, extra = ''): string =>
  `<svg class="${cls}" ${extra} viewBox="0 0 24 24" fill="none" stroke="currentColor" ` +
  `stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

// Lucide (lucide.dev): check, x, triangle-alert, lock, wifi-off, refresh-cw, cloud-off
const ICONS: Record<StatusIconName, string> = {
  check: '<path d="M20 6 9 17l-5-5"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  alert:
    '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/>' +
    '<path d="M12 9v4"/><path d="M12 17h.01"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  'wifi-off':
    '<path d="M12 20h.01"/><path d="M8.5 16.429a5 5 0 0 1 7 0"/><path d="M5 12.859a10 10 0 0 1 5.17-2.69"/>' +
    '<path d="M19 12.859a10 10 0 0 0-2.007-1.523"/><path d="M2 8.82a15 15 0 0 1 4.177-2.643"/>' +
    '<path d="M22 8.82a15 15 0 0 0-11.288-3.764"/><path d="m2 2 20 20"/>',
  refresh:
    '<path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/>' +
    '<path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/>',
  'cloud-off':
    '<path d="m2 2 20 20"/><path d="M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193"/>' +
    '<path d="M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07"/>',
};

// Antumo brand mark (web/assets/logos/antumo-icon-brand.svg), fill: currentColor.
const MARK =
  '<svg class="antumo-status-mark" viewBox="0 0 33 32" fill="currentColor" aria-hidden="true">' +
  '<path d="M32.4047 8.45267L32.0534 7.06186C32.0115 6.89778 31.8312 6.81101 31.679 6.88428L30.3893 7.49557C30.0514 7.65561 22.1971 11.4077 16.6477 15.6764C16.5521 15.7496 16.4216 15.7496 16.3259 15.6764C10.7604 11.3915 2.92639 7.65561 2.58982 7.49557L1.3 6.88428C1.14795 6.81236 0.967477 6.89778 0.925756 7.06186L0.574343 8.45267C0.503043 8.73599 -1.13404 15.4636 1.46294 22.7144C3.91586 29.5682 10.6163 32 15.6084 32C16.081 32 17.4004 31.9674 18.2444 31.8699L18.3696 31.8549C19.2999 31.7479 20.4577 31.6151 21.139 31.4646C24.0766 30.8152 29.3325 28.8157 31.5175 22.7144C34.1158 15.4609 32.476 8.73599 32.4047 8.45256V8.45267ZM3.03007 11.3686C3.04886 11.1801 3.25082 11.0703 3.4177 11.1571C7.38255 13.2013 15.5316 17.7139 18.3588 21.603C20.5614 24.6326 20.7109 26.2593 20.4483 27.0902C20.2302 27.7803 19.5544 28.3197 18.441 28.6939C18.099 28.8077 17.4528 28.9325 17.1943 28.9745C16.7124 29.0517 16.1779 29.0911 15.6084 29.0911C11.5534 29.0911 6.12645 27.1621 4.18113 21.7276C2.72308 17.6584 2.79044 13.7041 3.03142 11.3685H3.03007V11.3686ZM20.6893 19.8842C20.2463 19.2728 19.6715 18.6236 18.9768 17.9457C18.857 17.8292 18.8678 17.6313 19.0024 17.5324C22.5283 14.9242 26.8997 12.529 29.5667 11.1531C29.7337 11.0663 29.9357 11.1762 29.9544 11.3645C30.1968 13.6934 30.2641 17.6407 28.8007 21.7265C27.8919 24.2654 26.2024 26.1863 23.7709 27.4483C23.577 27.5485 23.3536 27.3846 23.3832 27.1676C23.6686 25.1547 22.7814 22.7607 20.6893 19.8842Z"/>' +
  '<path d="M16.8147 5.21147L20.5869 10.4357C20.6745 10.5564 20.8414 10.5835 20.9612 10.4954L22.8635 9.10325C22.9834 9.01513 23.0103 8.84701 22.9228 8.72506L16.7056 0.111853C16.5979 -0.0372843 16.3785 -0.0372843 16.2708 0.111853L10.055 8.72506C9.96748 8.84566 9.99436 9.01513 10.1142 9.1019L12.0165 10.4941C12.1364 10.5822 12.3046 10.5551 12.3921 10.4344L16.1631 5.21012C16.3233 4.98781 16.6531 4.98781 16.8147 5.21012V5.21147Z"/>' +
  '</svg>';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const clamp01 = (n: number): number => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

let nextId = 0;

type Resolved = Required<Omit<StatusOverlayOptions, 'container' | 'onCountdownElapsed' | 'icon' | 'progress' | 'countdown' | 'text'>> & {
  text: string;
  icon: StatusIconName | null;
  progress: number | null;
  countdown: number | null;
  container: HTMLElement | null;
  onCountdownElapsed?: (overlay: StatusOverlay) => void;
};

const DEFAULTS: Omit<Resolved, 'title'> = {
  text: '',
  tone: 'neutral',
  theme: 'light',
  layout: 'stacked',
  indicator: 'ring',
  progress: null,
  countdown: null,
  icon: null,
  showMark: true,
  edge: false,
  actions: [],
  container: null,
};

export class StatusOverlay {
  private static _instances: StatusOverlay[] = [];

  private _opts: Resolved;
  private _host: HTMLElement;
  private _card: HTMLElement;
  private _adopted: boolean;
  private _id = ++nextId;
  private _indicatorKey = '';
  private _remaining = 0;
  private _timer: ReturnType<typeof setInterval> | null = null;
  private _onCancel = (e: Event): void => e.preventDefault();

  private constructor(options: StatusOverlayOptions, host: HTMLElement | null) {
    if (!options || typeof options.title !== 'string' || options.title === '') {
      throw new TypeError('StatusOverlay: `title` is required');
    }
    this._opts = { ...DEFAULTS, ...options } as Resolved;
    this._adopted = host !== null;
    this._host = host ?? StatusOverlay._createHost(this._opts.container);
    this._card = document.createElement('div');
    this._card.className = 'antumo-status';
    this._card.tabIndex = -1;
    this._host.replaceChildren(this._card);
    this._host.classList.add('antumo-status-backdrop');
    // Escape must not close a blocking status (native <dialog> cancel).
    this._host.addEventListener('cancel', this._onCancel);
  }

  /** Create and show a new overlay. */
  static show(options: StatusOverlayOptions): StatusOverlay {
    const overlay = new StatusOverlay(options, null);
    overlay._render();
    overlay.open();
    return overlay;
  }

  /**
   * Adopt an existing element as the overlay host (e.g. a server-rendered <dialog>).
   * The element stays in the page after hide(); a <dialog> is closed, anything else gets `hidden`.
   */
  static attach(element: HTMLElement, options: StatusOverlayOptions): StatusOverlay {
    const existing = StatusOverlay._instances.find((o) => o._host === element);
    if (existing) {
      existing.update(options);
      existing.open();
      return existing;
    }
    const overlay = new StatusOverlay(options, element);
    overlay._render();
    overlay.open();
    return overlay;
  }

  static hideAll(): void {
    [...StatusOverlay._instances].forEach((o) => o.hide());
  }

  get element(): HTMLElement {
    return this._host;
  }

  get options(): Readonly<StatusOverlayOptions> {
    return this._opts;
  }

  /** Merge new options and re-render in place (same card, no entry animation). */
  update(options: Partial<StatusOverlayOptions>): StatusOverlay {
    const restartCountdown = 'countdown' in options;
    this._opts = { ...this._opts, ...options } as Resolved;
    if (restartCountdown) this._indicatorKey = '';
    this._render(restartCountdown);
    return this;
  }

  /** Show (again) after hide(). */
  open(): void {
    const host = this._host;
    host.hidden = false;
    if (!host.isConnected) (this._opts.container ?? document.body).appendChild(host);
    if (host instanceof HTMLDialogElement && !host.open) {
      try {
        host.showModal();
      } catch {
        host.setAttribute('open', '');
      }
    }
    if (!StatusOverlay._instances.includes(this)) StatusOverlay._instances.push(this);
    triggerEvent(host, EVENT_SHOWN);
  }

  hide(): void {
    this._stopTimer();
    StatusOverlay._instances = StatusOverlay._instances.filter((o) => o !== this);
    const host = this._host;
    if (host instanceof HTMLDialogElement && host.open) host.close();
    if (this._adopted) host.hidden = true;
    else host.remove();
    triggerEvent(host, EVENT_HIDDEN);
  }

  // ── rendering ───────────────────────────────────────────────────────────

  private static _createHost(container: HTMLElement | null): HTMLElement {
    // Viewport: <dialog> in the top layer (focus stays inside, the page is inert).
    // Container: plain div positioned over it.
    if (container) {
      const div = document.createElement('div');
      div.classList.add('antumo-status-backdrop--contained');
      return div;
    }
    return document.createElement('dialog');
  }

  private _effective(): {
    layout: StatusLayout;
    indicator: StatusIndicator;
    icon: StatusIconName | null;
  } {
    const o = this._opts;
    let layout = o.layout;
    if (layout === 'inline' && o.actions.length > 1) layout = 'stacked';
    const icon = o.icon ?? (o.tone === 'success' ? 'check' : o.tone === 'error' ? 'alert' : null);
    let indicator = o.indicator;
    if (layout === 'inline') indicator = 'ring';
    // Dots only say „waiting”: a result shows its icon, a measured wait uses the ring.
    if (indicator === 'dots' && icon) indicator = 'pulse';
    if (indicator === 'dots' && (o.progress != null || o.countdown != null)) indicator = 'ring';
    return { layout, indicator, icon };
  }

  private _render(restartCountdown = false): void {
    const o = this._opts;
    const { layout, indicator, icon } = this._effective();
    const card = this._card;
    const titleId = `antumo-status-title-${this._id}`;
    const textId = `antumo-status-text-${this._id}`;

    this._host.classList.toggle('antumo-status-backdrop--dark', o.theme === 'dark');

    card.className = [
      'antumo-status',
      `antumo-status--${o.tone}`,
      o.theme === 'dark' ? 'antumo-status--dark' : '',
      layout === 'inline' ? 'antumo-status--inline' : '',
    ]
      .filter(Boolean)
      .join(' ');
    const blocking = o.tone === 'error';
    card.setAttribute('role', blocking ? 'alertdialog' : 'status');
    card.setAttribute('aria-live', blocking ? 'assertive' : 'polite');
    card.setAttribute('aria-labelledby', titleId);
    card.setAttribute('aria-describedby', textId);
    card.setAttribute('aria-busy', String(!icon && o.progress == null));

    // Indicator: rebuilt only when its shape changes, so ring transitions and
    // spin animations are not restarted by every text update.
    const countdownOn = o.countdown != null && o.countdown > 0;
    const inner = icon ? `icon:${icon}` : o.showMark ? 'mark' : o.progress != null ? 'value' : countdownOn ? 'count' : 'none';
    const mode = icon ? 'full' : countdownOn ? 'count' : o.progress != null ? 'progress' : 'spin';
    const key = `${indicator}|${mode}|${inner}|${layout}`;

    let indicatorEl = card.querySelector<HTMLElement>(':scope > .antumo-status__indicator');
    if (key !== this._indicatorKey || !indicatorEl) {
      this._indicatorKey = key;
      const fresh = this._buildIndicator(indicator, mode, inner, icon, countdownOn);
      if (indicatorEl) indicatorEl.replaceWith(fresh);
      else card.prepend(fresh);
      indicatorEl = fresh;
    }
    if (indicatorEl.classList.contains('antumo-status-ring')) {
      const p = mode === 'full' ? 1 : mode === 'progress' ? clamp01(o.progress as number) : 1;
      indicatorEl.style.setProperty('--antumo-status-progress', String(p));
      if (countdownOn) indicatorEl.style.setProperty('--antumo-status-countdown', `${o.countdown}s`);
      const value = indicatorEl.querySelector('.antumo-status-ring__value:not([data-status-countdown])');
      if (value && o.progress != null) value.textContent = `${Math.round(clamp01(o.progress) * 100)}%`;
    }

    // Body
    let body = card.querySelector<HTMLElement>(':scope > .antumo-status__body');
    if (!body) {
      body = document.createElement('div');
      body.className = 'antumo-status__body';
      indicatorEl.after(body);
    }
    const textHtml = (o.text ?? '')
      .split('{s}')
      .map(escapeHtml)
      .join(`<b data-status-countdown>${countdownOn ? Math.ceil(o.countdown as number) : 0}</b>`);
    body.innerHTML =
      `<p class="antumo-status__title" id="${titleId}">${escapeHtml(o.title)}</p>` +
      `<p class="antumo-status__text" id="${textId}">${textHtml}</p>`;

    // Actions
    card.querySelector(':scope > .antumo-status__actions')?.remove();
    if (o.actions.length > 0) {
      const actions = document.createElement('div');
      actions.className = 'antumo-status__actions';
      o.actions.forEach((a) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `btn btn-${a.variant ?? 'secondary'}`;
        b.textContent = a.label;
        if (a.onClick) b.addEventListener('click', () => a.onClick?.(this));
        actions.appendChild(b);
      });
      body.after(actions);
    }

    // Edge bar
    card.querySelector(':scope > .antumo-status__edge')?.remove();
    if (o.edge) {
      const edge = document.createElement('div');
      edge.className = 'antumo-status__edge';
      if (mode === 'count') {
        edge.classList.add('is-countdown');
        edge.style.setProperty('--antumo-status-countdown', `${o.countdown}s`);
      } else if (mode === 'spin') edge.classList.add('is-indeterminate');
      else edge.style.setProperty('--antumo-status-progress', String(mode === 'full' ? 1 : clamp01(o.progress as number)));
      edge.innerHTML = '<i></i>';
      card.appendChild(edge);
    }

    // Countdown ticking (seconds into every [data-status-countdown])
    if (countdownOn) {
      if (restartCountdown || this._timer === null) this._startTimer(o.countdown as number);
      else this._paintCountdown();
    } else {
      this._stopTimer();
    }
  }

  private _buildIndicator(
    indicator: StatusIndicator,
    mode: string,
    inner: string,
    icon: StatusIconName | null,
    countdownOn: boolean,
  ): HTMLElement {
    const el = document.createElement('div');
    const innerHtml =
      inner.startsWith('icon:') && icon
        ? SVG(ICONS[icon], 'antumo-status-icon', `data-icon="${icon}"`)
        : inner === 'mark'
          ? MARK
          : inner === 'value'
            ? '<span class="antumo-status-ring__value"></span>'
            : inner === 'count'
              ? `<span class="antumo-status-ring__value" data-status-countdown>${countdownOn ? Math.ceil(this._opts.countdown as number) : ''}</span>`
              : '';

    if (indicator === 'dots') {
      el.className = 'antumo-status__indicator antumo-status-dots';
      el.innerHTML = '<span></span><span></span><span></span>';
    } else if (indicator === 'pulse') {
      el.className = 'antumo-status__indicator antumo-status-pulse';
      const waves = mode === 'full' ? '' : mode === 'count' ? '<span class="antumo-status-pulse__wave"></span>' : '<span class="antumo-status-pulse__wave"></span><span class="antumo-status-pulse__wave"></span>';
      el.innerHTML = waves + innerHtml;
    } else {
      el.className = `antumo-status__indicator antumo-status-ring${mode === 'spin' ? ' is-spinning' : ''}${mode === 'count' ? ' is-countdown' : ''}`;
      el.innerHTML =
        '<svg class="antumo-status-ring__track" viewBox="0 0 56 56" aria-hidden="true">' +
        '<circle class="antumo-status-ring__bg" cx="28" cy="28" r="24"/>' +
        `<circle class="antumo-status-ring__fg" cx="28" cy="28" r="24" stroke-dasharray="${RING_C}"/>` +
        '</svg>' +
        innerHtml;
    }
    return el;
  }

  private _startTimer(seconds: number): void {
    this._stopTimer();
    this._remaining = Math.ceil(seconds);
    this._paintCountdown();
    this._timer = setInterval(() => {
      this._remaining = Math.max(0, this._remaining - 1);
      this._paintCountdown();
      if (this._remaining === 0) {
        this._stopTimer();
        this._opts.onCountdownElapsed?.(this);
      }
    }, 1000);
  }

  private _stopTimer(): void {
    if (this._timer !== null) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  private _paintCountdown(): void {
    this._card
      .querySelectorAll('[data-status-countdown]')
      .forEach((n) => (n.textContent = String(this._remaining)));
  }
}
